/**
 * The contracts this project is covered by.
 *
 * Read-only about the contract itself, for the reason `ProjectInvoices` gives
 * about invoices: the document is owned by the contract section, and a second
 * place to edit a legal text is a second place for it to go wrong. The number is
 * a link into the contract, and everything drawn here - status tone, number over
 * title, the term, the agreed value - is the same row the register draws, so a
 * contract does not change appearance depending on which screen found it.
 *
 * The one thing this tab DOES write is the link itself, and only the link. See
 * `LinkToProjectModal` for why that has to live here: `Contract.projectId` is a
 * loose column the API accepts, and no contract form in this app draws a project
 * field, so without the button below the tab would describe a relationship
 * nobody can create. Unlinking is the button on the row, which is deliberately
 * the only way a contract leaves a project - it happens where the operator can
 * see which contract they are removing.
 *
 * ## Why this tab is handed the client as well as the project
 *
 * `projectBelongsToClient` (api, `crm-projects/lib/projectLink.ts`) refuses a
 * contract-to-project link whenever the two name different clients, with a 400
 * `project_client_mismatch`. So the picker has to be narrowed to this project's
 * client. That id is a PROP, from the record the page already holds, and not a
 * second `getProject` of our own: this tab's only write is the link, so gating
 * it on a redundant request means the primary action of the tab is missing for
 * as long as that request takes and missing for good if it fails - an empty card
 * whose one way forward is the button that never appeared. The expenses tab is
 * handed the same pair for the same reason.
 *
 * Offering the whole register and letting the server refuse would be a dialog
 * whose rows mostly cannot be clicked, which teaches nothing.
 *
 * ## Two permissions, not one
 *
 * The tab is inside `/projects`, which answers to `manage:crm.projects`. Writing
 * a contract answers to `manage:crm.contracts` (`contracts.routes.ts`), and the
 * second does not follow from the first. So the link and unlink controls are
 * gated on the contracts permission and are absent - not disabled - without it.
 * A button that renders and then 403s is worse than no button: it reads as a
 * broken feature rather than as one this operator does not have.
 *
 * ## The empty state is the normal state
 *
 * There are zero contracts in the whole CRM today, so every operator meets the
 * empty card first. It has to read as a finished answer, which is why it is an
 * `EmptyState` with a sentence and a way forward, and why "still loading" is a
 * bare spinner with no words in it at all. The two must not be confusable at a
 * glance.
 */
import { useCallback, useEffect, useState } from "react";
import { CalendarClock, FileSignature, Link2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import { getContracts, updateContract, type Contract } from "../../../lib/projects";
import { useCrmUser } from "../../shell/CrmUser";
import { Avatar, Btn, Card, CardHead, EmptyState, StatusPill, fmtDate, listCard, useToast } from "../../ui";
import { contractValue } from "../contracts/lib";
import { statusTone } from "../contracts/status";
import { LinkToProjectModal, UnlinkCell, type LinkCandidate } from "./LinkToProjectModal";

/** Number and title, status, value, end date, and the unlink control. Narrower
 *  than the register's grid because the client column is dropped: every contract
 *  on a project belongs to that project's client, so the column would repeat one
 *  name down the page. */
const COLS = "minmax(0,2.4fr) 1fr .9fr 1fr 44px";

/** The list route's own ceiling. A project with more than a hundred contracts on
 *  it is a data problem, not a paging problem, so this tab does not page. */
const MAX_ROWS = 100;

/** The term's far end, which is the date a project actually cares about: when
 *  the cover runs out. The start is on the contract's own page. */
function endCell(c: Contract): string {
  return c.endDate ? fmtDate(c.endDate) : "-";
}

export function ProjectContracts({
  projectId,
  clientId,
  onChange,
}: {
  projectId: string;
  /**
   * The project's client, for the picker below. Passed in rather than read
   * again here: see the header for why this tab must not gate its own write on
   * a second request.
   */
  clientId: string;
  /** Tells the page frame that the count on this tab's pill is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.contracts");
  // Null while the first read is in flight, so the spinner and the empty card
  // are two states and not one.
  const [rows, setRows] = useState<Contract[] | null>(null);
  const [linking, setLinking] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getContracts({ projectId, pageSize: MAX_ROWS });
      setRows(res.rows);
    } catch {
      // Said out loud, because the empty card below cannot say it: "no contracts
      // are linked to this project" is exactly what a failed read would draw
      // otherwise, and on a register where zero is the normal answer nobody
      // would ever doubt it.
      setRows([]);
      toast(t("common.error"));
    }
  }, [projectId, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // The candidates for the dialog: this client's contracts, flattened into the
  // shape it draws. The dialog itself drops the ones that already sit on a
  // project; the client filter is what keeps the rest from being refused.
  const candidates = useCallback(
    async (q: string): Promise<LinkCandidate[]> => {
      const res = await getContracts({ clientId, q: q || undefined, pageSize: MAX_ROWS });
      return res.rows.map((c) => ({
        id: c.id,
        title: c.title,
        number: c.number ?? t("contracts.noNumber"),
        statusLabel: t(`contracts.status.${c.status}`),
        tone: statusTone(c.status),
        projectId: c.projectId,
      }));
    },
    [clientId, t],
  );

  async function unlink(c: Contract) {
    setBusyId(c.id);
    try {
      await updateContract(c.id, { projectId: null });
      // Dropped locally rather than reloaded: the row is leaving this list and
      // nothing else on the card depends on the server's copy of it.
      setRows((prev) => (prev ?? []).filter((r) => r.id !== c.id));
      // The pill above this card counts the same rows, out of the summary. It is
      // not recomputed from the list, so a detach that did not say so would leave
      // a number over a card that disagrees with it for the rest of the session.
      onChange?.();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusyId(null);
    }
  }

  // Absent rather than disabled without the permission: a control that is there
  // but dead reads as broken, and this is "not for you", not "try again".
  const linkBtn = canManage ? (
    <Btn small tone="brand" onClick={() => setLinking(true)}>
      <Link2 className="size-4" />
      {t("projects.linkContract")}
    </Btn>
  ) : null;

  return (
    <>
      <Card style={listCard(COLS)}>
        <CardHead title={t("projects.linkedContracts")} actions={linkBtn} />

        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title={t("projects.noContracts")}
            body={t("projects.noContractsBody")}
            action={linkBtn}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
              <span>{t("contracts.number")}</span>
              <span>{t("contracts.statusCol")}</span>
              <span>{t("contracts.value")}</span>
              <span>{t("contracts.endDate")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((c) => (
                // A div, not a link: the row ends in a button, and interactive
                // content inside an <a> swallows its clicks. The number link
                // stretches an ::after over the row instead.
                <div key={c.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar name={c.title} />
                    <span className="min-w-0">
                      <Link
                        to={`/contracts/${c.id}`}
                        className="crm-prow-n crm-tnum crm-trunc block after:absolute after:inset-0 after:rounded-full"
                      >
                        {c.number ?? t("contracts.noNumber")}
                      </Link>
                      <span className="crm-prow-s crm-trunc block">{c.title}</span>
                    </span>
                  </span>

                  <span>
                    <StatusPill tone={statusTone(c.status)}>{t(`contracts.status.${c.status}`)}</StatusPill>
                  </span>

                  {/* The value agreed, not anything invoiced against it: the two
                      are not linked in the schema, so a "billed" figure here
                      would be a guess wearing a number's clothes. */}
                  <span className="crm-tnum text-[13.25px] font-bold">{contractValue(c.valueCents)}</span>

                  <span className="crm-mut crm-tnum flex items-center gap-1.5 text-[12.5px]">
                    <CalendarClock className="size-3.5 shrink-0" />
                    {endCell(c)}
                  </span>

                  <UnlinkCell
                    can={canManage}
                    busy={busyId === c.id}
                    disabled={busyId !== null}
                    onClick={() => void unlink(c)}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {linking ? (
        <LinkToProjectModal
          title={t("projects.linkContract")}
          searchPlaceholder={t("contracts.search")}
          fetchCandidates={candidates}
          link={(id) => updateContract(id, { projectId })}
          onClose={() => setLinking(false)}
          onLinked={() => {
            void load();
            onChange?.();
          }}
        />
      ) : null}
    </>
  );
}
